import React, { useCallback, useMemo, useState } from "react";

import DataTable from "@/components/Datatable/Datatable";
import MainPageWrapper from "@/components/MainPageWrapper/MainPageWrapper";
import CampaignJobStatusFilters from "@/modules/administration/features/Stochastic/CampaignJobStatus/components/CampaignJobStatusFilters/CampaignJobStatusFilters";
import UpdateBatchStatusModal from "@/modules/administration/features/Stochastic/CampaignJobStatus/components/UpdateBatchStatusModal/UpdateBatchStatusModal";
import DownloadCLIFileModal from "../DownloadCLIFileModal/DownloadCLIFileModal";
import WeekDisplay from "../WeekDisplay/WeekDisplay";
import { useCampaignJobStatusData } from "@/modules/administration/features/Stochastic/CampaignJobStatus/hooks/useCampaignJobStatusData";
import { useDownloadBatchesProspectsCsv } from "@/modules/administration/features/Stochastic/CampaignJobStatus/hooks/useDownloadBatchesProspectsCsv";
import { useBulkUpdateBatchesStatus } from "@/modules/administration/features/Stochastic/CampaignJobStatus/hooks/useBulkUpdateBatchesStatus";
import { useCreateBatchInvoices } from "@/modules/administration/features/Stochastic/CampaignJobStatus/hooks/useCreateBatchInvoices";
import { useDownloadSummaryCSV } from "@/modules/administration/features/Stochastic/CampaignJobStatus/hooks/useDownloadSummaryCSV";
import { createCampaignJobStatusColumns } from "@/modules/administration/features/Stochastic/CampaignJobStatus/components/CampaignJobStatusColumns/CampaignJobStatusColumns";
import { CampaignJobStatusDataRow } from "@/modules/administration/features/Stochastic/CampaignJobStatus/api/fetchCampaignJobStatusData/types";
import { useSendBatchProspectsToFtp } from "../../hooks/useSendBatchProspectsToFtp";

const CampaignJobStatusPage: React.FC = () => {
  const {
    campaignJobStatusDataRows,
    totalCount,
    mailDataRowsLoading,
    mailDataRowsError,
    campaignWeekEventData,
    cliFileStatus,
    batchStatuses,
    pagination,
    bulkBatchStatusDetailsMetadata,
    bulkBatchStatusDetailsMetadataLoading,
    filters,
    handlePaginationChange,
    handleFilterChange,
    sorting,
    handleSortingChange,
  } = useCampaignJobStatusData();
  const [showBillButton, setShowBillButton] = useState(false);
  const [checkedRows, setCheckedRows] = useState<Record<string, boolean>>({});
  const [isAllSelected, setIsAllSelected] = useState(false);
  const [modalState, setModalState] = useState({
    isOpen: false,
    selectedStatus: "new",
  });
  const [billingConfirmation, setBillingConfirmation] = useState({
    isOpen: false,
    count: 0,
  });
  const [downloadCLIModalOpen, setDownloadCLIModalOpen] = useState(false);

  const { loading: isDownloading, downloadCsv } =
    useDownloadBatchesProspectsCsv();
  const { loading: isBulkUpdating, bulkUpdateBatchesStatus } =
    useBulkUpdateBatchesStatus();
  const { loading: isBilling, billSelectedBatches } = useCreateBatchInvoices();
  const { loading: isDownloadingSummary, downloadSummaryCSV } =
    useDownloadSummaryCSV();
  const { loading: isSendingFtp, sendToFtp } = useSendBatchProspectsToFtp();

  const selectableRows = useMemo(() => {
    if (
      !campaignJobStatusDataRows ||
      !Array.isArray(campaignJobStatusDataRows)
    ) {
      return [];
    }
    return campaignJobStatusDataRows.filter(
      (row) =>
        row.batchStatus === "processed" &&
        row.batchPricing?.actualQuantity != null,
    );
  }, [campaignJobStatusDataRows]);

  const handleCheckboxChange = useCallback(
    (rowId: string, checked: boolean) => {
      setCheckedRows((prev) => {
        const newCheckedRows = {
          ...prev,
          [rowId]: checked,
        };

        const hasSelectedRows = Object.values(newCheckedRows).some(
          (isChecked) => isChecked,
        );
        setShowBillButton(hasSelectedRows);

        const allSelected = selectableRows.every(
          (row) => newCheckedRows[row.id.toString()] === true,
        );
        setIsAllSelected(allSelected);

        return newCheckedRows;
      });
    },
    [selectableRows],
  );

  const handleSelectAllChange = useCallback(
    (checked: boolean) => {
      const newCheckedRows = { ...checkedRows };

      selectableRows.forEach((row) => {
        newCheckedRows[row.id.toString()] = checked;
      });

      setCheckedRows(newCheckedRows);
      setIsAllSelected(checked);
      setShowBillButton(checked && selectableRows.length > 0);
    },
    [selectableRows, checkedRows],
  );

  const handleBillBatchesButtonClick = useCallback(() => {
    const selectedCount = Object.keys(checkedRows).filter(
      (id) => checkedRows[id],
    ).length;

    setBillingConfirmation({
      isOpen: true,
      count: selectedCount,
    });
  }, [checkedRows]);

  const confirmBillBatches = useCallback(async () => {
    try {
      await billSelectedBatches(checkedRows, campaignJobStatusDataRows);
      setCheckedRows({});
      setShowBillButton(false);
      setIsAllSelected(false);
      if (filters.week) {
        handleFilterChange("week", filters.week);
      }
    } catch (error) {
      console.error("Billing error:", error);
    } finally {
      setBillingConfirmation({ isOpen: false, count: 0 });
    }
  }, [
    billSelectedBatches,
    checkedRows,
    campaignJobStatusDataRows,
    filters.week,
    handleFilterChange,
  ]);

  const cancelBillBatches = useCallback(() => {
    setBillingConfirmation({ isOpen: false, count: 0 });
  }, []);

  const handleUpdateBatchStatusButtonClick = useCallback(
    () =>
      setModalState({
        isOpen: true,
        selectedStatus: bulkBatchStatusDetailsMetadata?.currentStatus || "new",
      }),
    [bulkBatchStatusDetailsMetadata],
  );

  const handleModalCloseButtonClicked = useCallback(
    () => setModalState((prev) => ({ ...prev, isOpen: false })),
    [],
  );

  const handleUpdateStatus = useCallback(
    (newStatus: string) =>
      setModalState((prev) => ({ ...prev, selectedStatus: newStatus })),
    [],
  );

  const handleDownloadCLIModalOpen = useCallback(async () => {
    if (campaignWeekEventData) {
      if (filters.week && filters.year) {
        await downloadCsv(filters.week as number, filters.year as number);
        if (filters.week) {
          handleFilterChange("week", filters.week);
        }
      }
    } else {
      setDownloadCLIModalOpen(true);
    }
  }, [campaignWeekEventData, downloadCsv, filters, handleFilterChange]);

  const handleDownloadCLIModalClose = useCallback(() => {
    setDownloadCLIModalOpen(false);
  }, []);

  const handleDownloadAndFinalize = useCallback(async () => {
    if (filters.week && filters.year) {
      await downloadCsv(filters.week as number, filters.year as number);
      setDownloadCLIModalOpen(false);
      if (filters.week) {
        handleFilterChange("week", filters.week);
      }
    }
  }, [downloadCsv, filters, handleFilterChange]);

  const handleDownloadSummary = useCallback(() => {
    if (filters.week && filters.year) {
      downloadSummaryCSV(filters.week as number, filters.year as number);
    }
  }, [downloadSummaryCSV, filters]);

  const handleSendToFtp = useCallback(async () => {
    if (!filters.week || !filters.year) return;

    try {
      await sendToFtp(filters.week as number, filters.year as number);
    } catch (error) {
      console.error("Error sending to FTP:", error);
    }
  }, [sendToFtp, filters]);

  const handleSaveButtonClicked = useCallback(async () => {
    if (!filters.week || !filters.year) return;

    await bulkUpdateBatchesStatus(
      filters.year as number,
      filters.week as number,
      modalState.selectedStatus,
    );

    handleModalCloseButtonClicked();
    handleFilterChange("week", filters.week);
  }, [
    bulkUpdateBatchesStatus,
    filters,
    modalState.selectedStatus,
    handleModalCloseButtonClicked,
    handleFilterChange,
  ]);

  const columns = useMemo(() => {
    const hasProcessedStatus = campaignJobStatusDataRows.some(
      (row) => row.batchStatus === "processed",
    );
    return createCampaignJobStatusColumns(
      hasProcessedStatus,
      checkedRows,
      handleCheckboxChange,
      isAllSelected,
      handleSelectAllChange,
    );
  }, [
    campaignJobStatusDataRows,
    checkedRows,
    handleCheckboxChange,
    isAllSelected,
    handleSelectAllChange,
  ]);

  const batchStatusOptions = useMemo(
    () => bulkBatchStatusDetailsMetadata?.bulkBatchStatusOptions || [],
    [bulkBatchStatusDetailsMetadata],
  );

  const isModalSaveButtonDisabled = useMemo(
    () =>
      modalState.selectedStatus ===
        bulkBatchStatusDetailsMetadata?.currentStatus || isBulkUpdating,
    [modalState.selectedStatus, bulkBatchStatusDetailsMetadata, isBulkUpdating],
  );

  return (
    <MainPageWrapper
      actions={<WeekDisplay />}
      error={mailDataRowsError}
      title="Campaign Job Status"
    >
      <CampaignJobStatusFilters
        batchStatuses={batchStatuses}
        bulkBatchStatusDetailsMetadata={bulkBatchStatusDetailsMetadata}
        campaignWeekEventData={campaignWeekEventData}
        cliFileStatus={cliFileStatus}
        filters={filters}
        isBilling={isBilling}
        isDataLoading={mailDataRowsLoading}
        isDownloading={isDownloading}
        isDownloadingSummary={isDownloadingSummary}
        isSendingToFtp={isSendingFtp}
        onBillBatchesButtonClick={handleBillBatchesButtonClick}
        onDownloadCLIFileModalOpen={handleDownloadCLIModalOpen}
        onDownloadSummaryButtonClick={handleDownloadSummary}
        onFilterChange={handleFilterChange}
        onSendToFtpButtonClick={handleSendToFtp}
        onUpdateBatchStatusButtonClick={handleUpdateBatchStatusButtonClick}
        showBillButton={showBillButton}
      />

      <DataTable<CampaignJobStatusDataRow>
        columns={columns}
        data={campaignJobStatusDataRows}
        error={mailDataRowsError}
        loading={mailDataRowsLoading || bulkBatchStatusDetailsMetadataLoading}
        noDataMessage="No campaign rows found"
        onPageChange={(newPageIndex, newPageSize) =>
          handlePaginationChange({
            pageIndex: newPageIndex,
            pageSize: newPageSize,
          })
        }
        onSortingChange={handleSortingChange}
        pageIndex={pagination.pageIndex}
        pageSize={pagination.pageSize}
        rowKeyExtractor={(row) =>
          row.id ?? `${row.campaignId}-${row.batchNumber}`
        }
        sorting={sorting}
        totalCount={totalCount}
      />

      <UpdateBatchStatusModal
        batchStatusOptions={batchStatusOptions}
        isModalOpen={modalState.isOpen}
        isSaveButtonDisabled={isModalSaveButtonDisabled}
        isSaving={isBulkUpdating}
        onCloseButtonClick={handleModalCloseButtonClicked}
        onSaveButtonClick={handleSaveButtonClicked}
        onStatusChange={handleUpdateStatus}
        selectedStatus={modalState.selectedStatus}
      />

      <DownloadCLIFileModal
        isDownloading={isDownloading}
        isModalOpen={downloadCLIModalOpen}
        onCloseButtonClick={handleDownloadCLIModalClose}
        onDownloadAndFinalizeClick={handleDownloadAndFinalize}
        week={(filters.week as number) ?? 1}
        year={(filters.year as number) ?? new Date().getFullYear()}
      />

      {billingConfirmation.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full">
            <h3 className="text-lg font-medium mb-4">Confirm Billing</h3>
            <p className="mb-2 text-lg">
              Are you sure you want to bill {billingConfirmation.count} selected
              batches?
            </p>
            <p className="mb-6 text-destructive">
              This action will invoice the selected batches for their campaigns
              and can not be undone.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                disabled={isBilling}
                onClick={cancelBillBatches}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                disabled={isBilling}
                onClick={confirmBillBatches}
              >
                {isBilling ? "Processing..." : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}
    </MainPageWrapper>
  );
};

export default CampaignJobStatusPage;
